import type { ApiResult } from './types';
import { caseApiRequest, dataPayloadErrorMessage, isDataPayloadSuccess } from './apiClient';

const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

export type CategoryAllocationUserOption = {
  userId: string;
  title: string;
  displayName: string;
  firstName: string;
  lastName: string;
  username: string;
  roles: string;
};

export type CategoryAllocationAllocatedUser = {
  caseRequestCategoryAllocationUserId: string;
  userId: string;
  title: string;
  displayName: string;
  firstName: string;
  lastName: string;
  roles: string;
};

export type CategoryAllocationSupervisor = {
  userId: string;
  title: string;
  displayName: string;
  firstName: string;
  lastName: string;
};

export type CategoryAllocationDetail = {
  categoryAllocationId: string;
  fileCategoryId: string;
  fileCategoryName: string;
  subFileCategoryId: string;
  subFileCategoryName: string;
  supervisor: CategoryAllocationSupervisor | null;
  stateAttorney: CategoryAllocationSupervisor | null;
  allocatedUserDetails: CategoryAllocationAllocatedUser[];
  createdAt: string;
  createdBy: string;
  createdByName: string;
  updatedAt: string;
  updatedBy: string;
  updatedByName: string;
};

type ApiUserRow = {
  user_id?: string;
  title?: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
  username?: string;
  roles?: string;
};

type ApiSupervisorRow = {
  user_id?: string;
  title?: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
};

type ApiAllocatedUserRow = {
  case_request_category_allocation_user_id?: string;
  user_id?: string;
  title?: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
  roles?: string;
};

type ApiCategoryAllocationRow = {
  category_allocation_id?: string;
  file_category_id?: string;
  file_category_name?: string;
  sub_file_category_id?: string;
  sub_file_category_name?: string;
  supervisor?: ApiSupervisorRow | null;
  state_attorney?: ApiSupervisorRow | null;
  allocated_user_details?: ApiAllocatedUserRow[];
  created_at?: string;
  created_by?: string;
  created_by_name?: string;
  updated_at?: string;
  updated_by?: string;
  updated_by_name?: string;
};

/** Data dictionary: display name with role, e.g. "Ms. Nethmi Perera - Counsel". */
export function formatCategoryAllocationUserLabel(user: { displayName: string; roles?: string; title?: string }): string {
  const name = user.displayName.trim() || [user.title?.trim(), ''].join(' ').trim();
  const roles = user.roles?.trim() ?? '';
  if (name && roles) return `${name} - ${roles}`;
  return name;
}

function normalizeUserOption(row: ApiUserRow): CategoryAllocationUserOption | null {
  const userId = str(row.user_id);
  if (!userId) return null;
  return {
    userId,
    title: str(row.title),
    displayName: str(row.display_name),
    firstName: str(row.first_name),
    lastName: str(row.last_name),
    username: str(row.username),
    roles: str(row.roles),
  };
}

function normalizeSupervisor(row: ApiSupervisorRow | null | undefined): CategoryAllocationSupervisor | null {
  if (!row || typeof row !== 'object') return null;
  const userId = str(row.user_id);
  if (!userId) return null;
  return {
    userId,
    title: str(row.title),
    displayName: str(row.display_name),
    firstName: str(row.first_name),
    lastName: str(row.last_name),
  };
}

function normalizeAllocatedUsers(data: unknown): CategoryAllocationAllocatedUser[] {
  if (!Array.isArray(data)) return [];
  const out: CategoryAllocationAllocatedUser[] = [];
  for (const item of data) {
    const r = (item && typeof item === 'object' ? (item as ApiAllocatedUserRow) : {}) as ApiAllocatedUserRow;
    const caseRequestCategoryAllocationUserId = str(r.case_request_category_allocation_user_id);
    const userId = str(r.user_id);
    if (!caseRequestCategoryAllocationUserId || !userId) continue;
    out.push({
      caseRequestCategoryAllocationUserId,
      userId,
      title: str(r.title),
      displayName: str(r.display_name),
      firstName: str(r.first_name),
      lastName: str(r.last_name),
      roles: str(r.roles),
    });
  }
  return out;
}

function normalizeCategoryAllocationDetail(data: unknown): CategoryAllocationDetail | null {
  if (!data || typeof data !== 'object') return null;
  const r = data as ApiCategoryAllocationRow;
  const categoryAllocationId = str(r.category_allocation_id);
  const fileCategoryId = str(r.file_category_id);
  const subFileCategoryId = str(r.sub_file_category_id);
  // Sub file category is optional for categories like Right to Information and Public Petitions.
  if (!categoryAllocationId || !fileCategoryId) return null;
  return {
    categoryAllocationId,
    fileCategoryId,
    fileCategoryName: str(r.file_category_name),
    subFileCategoryId,
    subFileCategoryName: str(r.sub_file_category_name),
    supervisor: normalizeSupervisor(r.supervisor),
    stateAttorney: normalizeSupervisor(r.state_attorney),
    allocatedUserDetails: normalizeAllocatedUsers(r.allocated_user_details),
    createdAt: str(r.created_at),
    createdBy: str(r.created_by),
    createdByName: str(r.created_by_name),
    updatedAt: str(r.updated_at),
    updatedBy: str(r.updated_by),
    updatedByName: str(r.updated_by_name),
  };
}

/** CMS-AUQ-7 — active intranet meta users for User's Name(s) dropdown. */
export async function listActiveMetaUsers(input?: { signal?: AbortSignal }): Promise<ApiResult<CategoryAllocationUserOption[]>> {
  const res = await caseApiRequest<unknown>('/v1/case-requests/users/active-meta-users', { signal: input?.signal });
  if (!res.ok) return { success: false, message: res.message };
  const raw = Array.isArray(res.data) ? res.data : [];
  const data = raw.map((row) => normalizeUserOption((row && typeof row === 'object' ? row : {}) as ApiUserRow)).filter((x): x is CategoryAllocationUserOption => x != null);
  return { success: true, data };
}

/** CMS-AUQ-8 — supervising officers for Supervisor dropdown. */
export async function listSupervisingOfficers(input?: { signal?: AbortSignal }): Promise<ApiResult<CategoryAllocationUserOption[]>> {
  const res = await caseApiRequest<unknown>('/v1/case-requests/users/supervising-officers', { signal: input?.signal });
  if (!res.ok) return { success: false, message: res.message };
  const raw = Array.isArray(res.data) ? res.data : [];
  const data = raw.map((row) => normalizeUserOption((row && typeof row === 'object' ? row : {}) as ApiUserRow)).filter((x): x is CategoryAllocationUserOption => x != null);
  return { success: true, data };
}

/** CMS-AUQ-9 - State attorneys for State Attorney filter dropdown. */
export async function listStateAttorneys(input?: { signal?: AbortSignal }): Promise<ApiResult<CategoryAllocationUserOption[]>> {
  const res = await caseApiRequest<unknown>('/v1/case-requests/users/state-attorneys', { signal: input?.signal });
  if (!res.ok) return { success: false, message: res.message };
  const raw = Array.isArray(res.data) ? res.data : [];
  const data = raw.map((row) => normalizeUserOption((row && typeof row === 'object' ? row : {}) as ApiUserRow)).filter((x): x is CategoryAllocationUserOption => x != null);
  return { success: true, data };
}

/** CMS-CAS-2 — category allocation details by id. */
export async function getCategoryAllocationById(input: {
  categoryAllocationId: string;
  signal?: AbortSignal;
}): Promise<ApiResult<CategoryAllocationDetail>> {
  const id = input.categoryAllocationId.trim();
  if (!id) return { success: false, message: 'Category allocation id is required.' };
  const res = await caseApiRequest<unknown>(`/v1/category-allocation-setup/${encodeURIComponent(id)}`, {
    signal: input.signal,
  });
  if (!res.ok) return { success: false, message: res.message };
  const detail = normalizeCategoryAllocationDetail(res.data);
  if (!detail) return { success: false, message: 'Invalid category allocation response.' };
  return { success: true, data: detail };
}

/** CMS-CAS-3 — create category allocation. */
export async function createCategoryAllocation(body: {
  fileCategoryId: string;
  subFileCategoryId: string;
  supervisorId: string;
  stateAttorneyId: string;
  userIds: string[];
}): Promise<ApiResult<{ categoryAllocationId: string; message: string }>> {
  const res = await caseApiRequest<unknown>('/v1/category-allocation-setup', {
    method: 'POST',
    body: {
      file_category_id: body.fileCategoryId.trim(),
      sub_file_category_id: body.subFileCategoryId.trim(),
      supervisor_id: body.supervisorId.trim(),
      state_attorney_id: body.stateAttorneyId.trim(),
      user_ids: body.userIds.map((id) => id.trim()).filter(Boolean),
    },
  });
  if (!res.ok) return { success: false, message: res.message };
  if (!isDataPayloadSuccess(res.data)) return { success: false, message: dataPayloadErrorMessage(res.data) };
  const row = (res.data && typeof res.data === 'object' ? res.data : {}) as Record<string, unknown>;
  const categoryAllocationId = str(row.category_allocation_id ?? row.categoryAllocationId);
  const message = str(row.message) || 'User category created successfully.';
  if (!categoryAllocationId) return { success: false, message: 'Invalid create response.' };
  return { success: true, data: { categoryAllocationId, message } };
}

export type CaseRequestRoleOption = {
  id: number;
  name: string;
  metaCode: string;
};

export type CategoryAllocationSearchRow = {
  categoryAllocationId: string;
  fileCategoryName: string;
  subFileCategoryName: string;
  supervisorName: string;
  stateAttorneyName: string;
  lastUpdatedAt: string;
  allocatedUsers: { name: string; roles: string }[];
};

/** Flattened table row — one entry per allocated user (manage screen). */
export type UserCategoryListRow = {
  id: string;
  categoryAllocationId: string;
  fileCategory: string;
  subFileCategory: string;
  userName: string;
  userRoles: string;
  supervisor: string;
  stateAttorney: string;
  lastUpdatedAt: string;
};

type ApiSearchSupervisorRow = {
  user_id?: string;
  name?: string;
  display_name?: string;
  roles?: string;
};

type ApiSearchAllocatedUserRow = {
  name?: string;
  display_name?: string;
  roles?: string;
};

type ApiSearchResultRow = {
  category_allocation_id?: string;
  file_category_name?: string;
  sub_file_category_name?: string;
  supervisor?: ApiSearchSupervisorRow | null;
  state_attorney?: ApiSearchSupervisorRow | null;
  last_updated_at?: string;
  allocated_users?: ApiSearchAllocatedUserRow[];
};

function normalizeSearchResultRow(row: ApiSearchResultRow): CategoryAllocationSearchRow | null {
  const categoryAllocationId = str(row.category_allocation_id);
  if (!categoryAllocationId) return null;
  const supervisorRow = row.supervisor;
  const supervisorName = str(supervisorRow?.name) || str(supervisorRow?.display_name);
  const stateAttorneyRow = row.state_attorney;
  const stateAttorneyName = str(stateAttorneyRow?.name) || str(stateAttorneyRow?.display_name);
  const allocatedUsers: { name: string; roles: string }[] = [];
  if (Array.isArray(row.allocated_users)) {
    for (const u of row.allocated_users) {
      const r = (u && typeof u === 'object' ? u : {}) as ApiSearchAllocatedUserRow;
      const name = str(r.name) || str(r.display_name);
      if (!name) continue;
      allocatedUsers.push({ name, roles: str(r.roles) });
    }
  }
  return {
    categoryAllocationId,
    fileCategoryName: str(row.file_category_name),
    subFileCategoryName: str(row.sub_file_category_name),
    supervisorName,
    stateAttorneyName,
    lastUpdatedAt: str(row.last_updated_at),
    allocatedUsers,
  };
}

function extractSearchResults(data: unknown): CategoryAllocationSearchRow[] {
  if (!data || typeof data !== 'object') return [];
  const root = data as Record<string, unknown>;
  const raw = Array.isArray(root.results) ? root.results : Array.isArray(data) ? data : [];
  const out: CategoryAllocationSearchRow[] = [];
  for (const item of raw) {
    const row = normalizeSearchResultRow((item && typeof item === 'object' ? item : {}) as ApiSearchResultRow);
    if (row) out.push(row);
  }
  return out;
}

export function flattenCategoryAllocationSearchResults(results: CategoryAllocationSearchRow[]): UserCategoryListRow[] {
  const rows: UserCategoryListRow[] = [];
  for (const r of results) {
    const subFileCategory = r.subFileCategoryName.trim() || '\u2014';
    const supervisor = r.supervisorName.trim() || '\u2014';
    const stateAttorney = r.stateAttorneyName.trim() || '\u2014';
    if (r.allocatedUsers.length === 0) {
      rows.push({
        id: `${r.categoryAllocationId}-0`,
        categoryAllocationId: r.categoryAllocationId,
        fileCategory: r.fileCategoryName,
        subFileCategory,
        userName: '\u2014',
        userRoles: '\u2014',
        supervisor,
        stateAttorney,
        lastUpdatedAt: r.lastUpdatedAt,
      });
      continue;
    }
    r.allocatedUsers.forEach((u, index) => {
      rows.push({
        id: `${r.categoryAllocationId}-${index}`,
        categoryAllocationId: r.categoryAllocationId,
        fileCategory: r.fileCategoryName,
        subFileCategory,
        userName: u.name,
        userRoles: u.roles.trim() || '\u2014',
        supervisor,
        stateAttorney,
        lastUpdatedAt: r.lastUpdatedAt,
      });
    });
  }
  rows.sort((a, b) => {
    const fc = a.fileCategory.localeCompare(b.fileCategory);
    if (fc !== 0) return fc;
    const sfc = a.subFileCategory.localeCompare(b.subFileCategory);
    if (sfc !== 0) return sfc;
    return a.userName.localeCompare(b.userName);
  });
  return rows;
}

/** CMS-MR-1 — active roles for User Role filter dropdown. */
export async function listCaseRequestRoles(input?: { signal?: AbortSignal }): Promise<ApiResult<CaseRequestRoleOption[]>> {
  const res = await caseApiRequest<unknown>('/v1/case-requests/roles', { signal: input?.signal });
  if (!res.ok) return { success: false, message: res.message };
  const raw = Array.isArray(res.data) ? res.data : [];
  const data: CaseRequestRoleOption[] = [];
  for (const item of raw) {
    const r = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
    const id = typeof r.id === 'number' ? r.id : Number(r.id);
    const name = str(r.name);
    if (!Number.isFinite(id) || !name) continue;
    data.push({ id, name, metaCode: str(r.meta_code ?? r.metaCode) });
  }
  data.sort((a, b) => a.name.localeCompare(b.name));
  return { success: true, data };
}

/** CMS-CAS-5 — search category allocations. */
export async function searchCategoryAllocations(input: {
  fileCategoryId?: string;
  subFileCategoryId?: string;
  userId?: string;
  userRoleId?: number;
  supervisorId?: string;
  stateAttorneyId?: string;
  signal?: AbortSignal;
}): Promise<ApiResult<UserCategoryListRow[]>> {
  const body: Record<string, unknown> = {};
  const fileCategoryId = input.fileCategoryId?.trim();
  const subFileCategoryId = input.subFileCategoryId?.trim();
  const userId = input.userId?.trim();
  const supervisorId = input.supervisorId?.trim();
  const stateAttorneyId = input.stateAttorneyId?.trim();
  if (fileCategoryId) body.file_category_id = fileCategoryId;
  if (subFileCategoryId) body.sub_file_category_id = subFileCategoryId;
  if (userId) body.user_id = userId;
  if (typeof input.userRoleId === 'number' && Number.isFinite(input.userRoleId)) body.user_role_id = input.userRoleId;
  if (supervisorId) body.supervisor_id = supervisorId;
  if (stateAttorneyId) body.state_attorney_id = stateAttorneyId;

  const res = await caseApiRequest<unknown>('/v1/category-allocation-setup/search', {
    method: 'POST',
    body,
    signal: input.signal,
  });
  if (!res.ok) return { success: false, message: res.message };
  if (!isDataPayloadSuccess(res.data)) return { success: false, message: dataPayloadErrorMessage(res.data) };
  const results = extractSearchResults(res.data);
  return { success: true, data: flattenCategoryAllocationSearchResults(results) };
}

/** CMS-CAS-4 — update category allocation. */
export async function updateCategoryAllocation(input: {
  categoryAllocationId: string;
  supervisorId: string;
  stateAttorneyId: string;
  userIds: string[];
}): Promise<ApiResult<{ categoryAllocationId: string; message: string }>> {
  const id = input.categoryAllocationId.trim();
  if (!id) return { success: false, message: 'Category allocation id is required.' };
  const res = await caseApiRequest<unknown>(`/v1/category-allocation-setup/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: {
      supervisor_id: input.supervisorId.trim(),
      state_attorney_id: input.stateAttorneyId.trim(),
      user_ids: input.userIds.map((uid) => uid.trim()).filter(Boolean),
    },
  });
  if (!res.ok) return { success: false, message: res.message };
  if (!isDataPayloadSuccess(res.data)) return { success: false, message: dataPayloadErrorMessage(res.data) };
  const row = (res.data && typeof res.data === 'object' ? res.data : {}) as Record<string, unknown>;
  const categoryAllocationId = str(row.category_allocation_id ?? row.categoryAllocationId) || id;
  const message = str(row.message) || 'User category updated successfully.';
  return { success: true, data: { categoryAllocationId, message } };
}
